import { createApi } from "@reduxjs/toolkit/query/react";
import { VITE_INVOICE_FINANCE_URL } from "codes";
import { axiosBaseQuery } from "helpers/axiosbaseQuery";

export type InvoiceFinanceAction =
  | "fund"
  | "buyback"
  | "close"
  | "close_passthrough";

export interface InvoiceFinancePreviewPayload {
  preview_url?: string;
  url?: string;
  preview_html?: string;
  html?: string;
  preview_pdf_base64?: string;
  pdf_base64?: string;
  message?: string;
  [key: string]: unknown;
}

export interface InvoiceFinanceActionResponse {
  success?: boolean;
  message?: string;
  phase?: string;
  status?: string;
  [key: string]: unknown;
}

export interface FeeInvoiceResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    invoice_id: string;
    invoice_reference_number: string;
    tax_invoice_type: string;
    currency: string;
    invoice_amount: number;
    gst_amount: number;
    total_amount: number;
    document_reference: string;
    download_path: string;
  };
}

export interface InvoiceEmailRecord {
  id: string;
  email_key: string;
  funder_id?: string | null;
  funder_name?: string | null;
  allocation_id?: string | null;
  sf_allocation_number?: string | null;
  subject?: string;
  to?: string[];
  cc?: string[];
  body?: string;
  status?: string;
  created_at?: string;
  sent_at?: string | null;
  is_test?: boolean;
  is_simulated?: boolean;
}

export interface InvoiceEmailDelivery {
  allocation_id?: string | null;
  sf_allocation_number?: string | null;
  funder_id?: string | null;
  funder_name?: string | null;
  recipient_name?: string | null;
  to?: string[];
  cc?: string[];
  status: boolean;
  needs_send: boolean;
  requires_review: boolean;
  recipient_available?: boolean;
  reason?: string | null;
}

export interface InvoiceEmailCategory {
  category: string;
  email_key: string;
  event: string;
  party: string;
  party_name?: string | null;
  eligible: boolean;
  status: boolean;
  needs_send: boolean;
  tracking_complete: boolean;
  requires_review: boolean;
  reason?: string | null;
  deliveries?: InvoiceEmailDelivery[];
  emails?: InvoiceEmailRecord[];
}

export interface InvoiceEmailEligibility {
  invoice_id: string;
  buyer_name?: string | null;
  supplier_name?: string | null;
  funders?: Array<{
    funder_id?: string | null;
    funder_name?: string | null;
    allocation_id?: string | null;
    sf_allocation_number?: string | null;
  }>;
  product?: string;
  phase?: string;
  is_test?: boolean;
  categories: InvoiceEmailCategory[];
  emails?: InvoiceEmailRecord[];
}

interface InvoiceFinanceActionParams {
  buyback_date?: string;
  funding_date?: string;
  repayment_date?: string;
}

export type SalesforceInvoiceAction = "fund" | "buyback" | "close";

const SALESFORCE_INVOICE_ACTION_PATHS: Record<SalesforceInvoiceAction, string> = {
  fund: "invoice-is-funded",
  buyback: "add-update-allocation",
  close: "invoice-is-repaid",
};

const ACTION_PATHS: Record<
  InvoiceFinanceAction,
  { execute: string; preview: string }
> = {
  fund: { execute: "fund", preview: "fund-preview" },
  buyback: { execute: "buyback", preview: "buyback-preview" },
  close: { execute: "close", preview: "close-preview" },
  close_passthrough: {
    execute: "close-passthrough",
    preview: "close-passthrough-preview",
  },
};

const extractResponseData = (response: any) => response?.data ?? response ?? {};

const normalizeBaseUrl = (baseUrl: string) => {
  const trimmedBaseUrl = String(baseUrl ?? "").trim().replace(/\/+$/, "");

  if (!trimmedBaseUrl) {
    return "";
  }

  return trimmedBaseUrl.endsWith("/api/v2")
    ? trimmedBaseUrl
    : `${trimmedBaseUrl}/api/v2`;
};

export const invoiceFinanceApi = createApi({
  reducerPath: "invoiceFinanceApi",
  tagTypes: ["INVOICE_FINANCE_ACTION", "INVOICE_EMAIL_ELIGIBILITY"],
  baseQuery: axiosBaseQuery({
    baseUrl: normalizeBaseUrl(VITE_INVOICE_FINANCE_URL),
  }),
  endpoints: (builder) => ({
    getClientFundingPreview: builder.query<
      Record<string, unknown>,
      string
    >({
      query: (invoiceId) => ({
        url: `/invoices/${encodeURIComponent(invoiceId)}/client-funding-preview`,
        method: "GET",
      }),
      transformResponse: (response: any) => response,
    }),
    previewInvoiceFinanceAction: builder.mutation<
      InvoiceFinancePreviewPayload,
      {
        invoiceId: string;
        action: InvoiceFinanceAction;
        params?: InvoiceFinanceActionParams;
      }
    >({
      query: ({ invoiceId, action, params }) => ({
        url: `/invoices/${encodeURIComponent(invoiceId)}/${ACTION_PATHS[action].preview}`,
        method: "GET",
        params,
      }),
      transformResponse: (response: any) => extractResponseData(response),
    }),

    SyncInvoiceSalesforceAction: builder.mutation<
      any,
      {
        invoice_id: string;
        sf_invoice_id: string;
        action: SalesforceInvoiceAction;
      }
    >({
      query: ({ invoice_id, sf_invoice_id, action }) => ({
        url: `/invoices/${encodeURIComponent(invoice_id)}/salesforce/${SALESFORCE_INVOICE_ACTION_PATHS[action]}`,
        method: "POST",
        data: {
          inputs: [{ invoiceID: sf_invoice_id }],
        },
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: ["INVOICE_FINANCE_ACTION"],
      transformResponse: (response) => response?.data ?? response,
    }),

    executeInvoiceFinanceAction: builder.mutation<
      InvoiceFinanceActionResponse,
      {
        invoiceId: string;
        action: InvoiceFinanceAction;
        params?: InvoiceFinanceActionParams;
      }
    >({
      query: ({ invoiceId, action, params }) => ({
        url: `/invoices/${ACTION_PATHS[action].execute}`,
        method: "POST",
        data: {
          invoice_id: invoiceId,
          ...params,
        },
      }),
      transformResponse: (response: any) => extractResponseData(response),
      invalidatesTags: ["INVOICE_FINANCE_ACTION"],
    }),
    getTaxInvoices: builder.query<any, string>({
      query: (invoiceId) => ({
        url: `/invoices/${encodeURIComponent(invoiceId)}/tax-invoices`,
        method: "GET",
      }),
      transformResponse: (response: any) => extractResponseData(response),
    }),
    createFeeInvoice: builder.mutation<FeeInvoiceResponse, string>({
      query: (invoiceId) => ({
        url: `/invoices/${encodeURIComponent(invoiceId)}/fee-invoice`,
        method: "POST",
      }),
      transformResponse: (response: FeeInvoiceResponse | { data: FeeInvoiceResponse }) =>
        "success" in response ? response : response.data,
    }),
    getTaxInvoiceDownload: builder.mutation<
      Blob,
      { invoiceId: string; taxInvoiceId: string }
    >({
      query: ({ invoiceId, taxInvoiceId }) => ({
        url: `/invoices/${encodeURIComponent(invoiceId)}/tax-invoices/${encodeURIComponent(taxInvoiceId)}/download`,
        method: "GET",
        responseType: "blob",
        headers: { Accept: "application/pdf" },
      }),
    }),
    getFeeInvoiceDownload: builder.mutation<
      Blob,
      { downloadPath: string }
    >({
      query: ({ downloadPath }) => ({
        url: downloadPath.replace(/^\/api\/v2(?=\/)/, ""),
        method: "GET",
        responseType: "blob",
        headers: { Accept: "application/pdf" },
      }),
    }),
    sendTaxInvoiceEmail: builder.mutation<
      InvoiceFinanceActionResponse,
      { invoiceId: string; taxInvoiceId: string }
    >({
      query: ({ invoiceId, taxInvoiceId }) => ({
        url: `/invoices/${encodeURIComponent(invoiceId)}/tax-invoices/${encodeURIComponent(taxInvoiceId)}/send-email`,
        method: "POST",
      }),
      transformResponse: (response: any) => extractResponseData(response),
    }),
    getInvoiceEmailEligibility: builder.query<InvoiceEmailEligibility, string>({
      query: (invoiceId) => ({
        url: `/invoices/${encodeURIComponent(invoiceId)}/email-eligibility`,
        method: "GET",
      }),
      transformResponse: (response: any) => extractResponseData(response),
      providesTags: ["INVOICE_EMAIL_ELIGIBILITY"],
    }),
    sendInvoiceEmail: builder.mutation<
      InvoiceFinanceActionResponse,
      { invoice_id: string; email_key: string; allocation_id?: string }
    >({
      query: (data) => ({
        url: "/invoices/send-email",
        method: "POST",
        data,
      }),
      transformResponse: (response: any) => response,
      invalidatesTags: ["INVOICE_EMAIL_ELIGIBILITY"],
    }),
  }),
});

export const {
  useLazyGetClientFundingPreviewQuery,
  usePreviewInvoiceFinanceActionMutation,
  useExecuteInvoiceFinanceActionMutation,
  useGetTaxInvoicesQuery,
  useCreateFeeInvoiceMutation,
  useGetTaxInvoiceDownloadMutation,
  useGetFeeInvoiceDownloadMutation,
  useSendTaxInvoiceEmailMutation,
  useSyncInvoiceSalesforceActionMutation,
  useGetInvoiceEmailEligibilityQuery,
  useSendInvoiceEmailMutation,
} = invoiceFinanceApi;

// RTK Query's reducer, middleware, and endpoint definitions must come from the
// same API instance. Reload the page when Vite replaces this module.
if (import.meta.hot) {
  import.meta.hot.accept(() => window.location.reload());
}
